import { useEffect, useRef, useState } from "react";

/*
 * Count-up animation for a numeric target, used by the Telegram connect gate's
 * progress readout. Lives in `src/hooks/` rather than beside one component so
 * the next count-up does not hand-roll a second rAF loop.
 */
export function useTween(target: number, duration = 900): number {
  const [value, setValue] = useState(0);
  const valueRef = useRef(0);
  valueRef.current = value;
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || duration <= 0) {
      setValue(target);
      return;
    }
    const from = valueRef.current;
    let startTs = 0;
    const step = (ts: number) => {
      if (!startTs) startTs = ts;
      const p = Math.min(1, (ts - startTs) / duration);
      const eased = 1 - (1 - p) ** 3;
      setValue(from + (target - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);
  return value;
}
